import * as Sentry from "@sentry/react";
import * as Comlink from "comlink";
import EventEmitter from "eventemitter3";
import { action, runInAction } from "mobx";
import React, { useEffect } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { setDeviceId } from "../Amplitude";
import { PARENT_ORIGIN } from "../Constants";
import type { ParentComlinkAPIType } from "../parent/FrameParentAPI";
import { CanvasData, SetCanvasOptions } from "../Types";
import {
  canvasViewport,
  initialSearchParamEntriesBox,
  isSafeModeAtom,
  urlLayoutIdAtom,
  usedDefaultLayoutAtom,
} from "./CanvasState";

export function useClosePopperSandbox(
  setPopperVisibility: React.Dispatch<React.SetStateAction<boolean>>
) {
  useEffect(() => {
    function onSandboxMouseDown() {
      setPopperVisibility(false);
    }
    ParentIframeEventEmitter.addListener("BODY_MOUSEDOWN", onSandboxMouseDown);
    return () => {
      ParentIframeEventEmitter.removeListener(
        "BODY_MOUSEDOWN",
        onSandboxMouseDown
      );
    };
  }, []);
}

export const ParentIframeEventEmitter = new EventEmitter();

export function initializeSandboxFrame(
  setLoadError: React.Dispatch<React.SetStateAction<Error | undefined>>,
  setCanvasData: (canvasData: CanvasData | undefined) => void
): () => void {
  function onMessage(message: MessageEvent) {
    switch (message.data.type) {
      case "SET_DEVICE_ID": {
        setDeviceId(message.data.deviceId);
        break;
      }
      case "SET_CANVAS": {
        const {
          canvasData,
          layoutId,
          options,
        }: {
          canvasData: CanvasData;
          layoutId: string | undefined;
          options: SetCanvasOptions;
        } = message.data;
        Sentry.setContext("parent", {
          canvas_sync_id: canvasData[0] === "sync" ? canvasData[1] : undefined,
        });
        try {
          unstable_batchedUpdates(
            action(() => {
              setLoadError(undefined);
              setCanvasData(canvasData);
              if (options.isSafeMode === true) {
                isSafeModeAtom.value = true;
              }
              if (options.initialViewport !== undefined) {
                const [x, y, zoom] = options.initialViewport;
                canvasViewport.offset = [x, y];
                canvasViewport.zoom = zoom;
              }
              if (options.initialSearchParamEntries !== undefined) {
                initialSearchParamEntriesBox.set(
                  options.initialSearchParamEntries
                );
              }
              if (options.usedDefaultLayout === true) {
                usedDefaultLayoutAtom.value = true;
              }
              urlLayoutIdAtom.value = layoutId;
            })
          );
        } catch (e: any) {
          setLoadError(e);
        }
        break;
      }
      case "SET_LAYOUT_ID": {
        const {
          layoutId,
        }: {
          layoutId: string | undefined;
        } = message.data;
        runInAction(() => {
          urlLayoutIdAtom.value = layoutId;
        });
        break;
      }
      case "BODY_KEYDOWN": {
        ParentIframeEventEmitter.emit("BODY_KEYDOWN", {
          code: message.data.code,
          key: message.data.key,
          altKey: message.data.altKey,
          shiftKey: message.data.shiftKey,
          metaKey: message.data.metaKey,
          ctrlKey: message.data.ctrlKey,
        });
        break;
      }
      case "BODY_KEYUP": {
        ParentIframeEventEmitter.emit("BODY_KEYUP", {
          code: message.data.code,
          key: message.data.key,
        });
        break;
      }
      case "BODY_MOUSEDOWN": {
        ParentIframeEventEmitter.emit("BODY_MOUSEDOWN");
        break;
      }
      case "ENVIRONMENT_VARIABLE_CHANGE": {
        ParentIframeEventEmitter.emit("ENVIRONMENT_VARIABLE_CHANGE", {
          variableKey: message.data.variableKey,
        });
        break;
      }
    }
  }
  window.addEventListener("message", onMessage);

  function onBodyMouseDown() {
    window.parent.postMessage({ type: "BODY_MOUSEDOWN" }, PARENT_ORIGIN);
  }
  document.body.addEventListener("mousedown", onBodyMouseDown);

  window.parent.postMessage({ type: "SANDBOX_INIT" }, PARENT_ORIGIN);

  return () => {
    window.removeEventListener("message", onMessage);
    document.body.removeEventListener("mousedown", onBodyMouseDown);
  };
}

// @ts-ignore
export const ParentComlinkAPI: ParentComlinkAPIType = Comlink.wrap(
  Comlink.windowEndpoint(window.parent!)
);
